import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import GoogleMapReact from 'google-map-react';
import useSupercluster from 'use-supercluster';

import clusterMarker from 'assets/clusterCircle.svg';
import MapCommunityCard from 'components/components/molecules/MapCommunityCard';

const API_KEY = 'AIzaSyBxwE18w5jvbH6hNpo2szfaM_f7EcvxeNU';
const MAP_ID = 'dcc65d7cb6edfa3d23f5c7a3'; // 3e74773f4234edfb

const defaultMapOptions = {
  clickableIcons: false,
  fullscreenControl: false,
  mapTypeControl: false,
  cameraControl: false,
  zoomControlOptions: {
    position: 9,
  },
  styles: [{ featureType: 'poi', stylers: [{ visibility: 'off' }] }],
  mapId: MAP_ID,
};

const isValidCoordinate = (lat, lng) =>
  !isNaN(parseFloat(lat)) &&
  !isNaN(parseFloat(lng)) &&
  Math.abs(parseFloat(lat)) <= 90 &&
  Math.abs(parseFloat(lng)) <= 180;

const containerStyle = {
  position: 'relative',
  height: '100%',
  width: '100%',
  borderRadius: '12px',
  overflow: 'hidden',
};

const CustomMarker = ({ children, ...props }) => (
  <div
    {...props}
    style={{
      position: 'absolute',
      transform: 'translate(-50%, -100%)',
      textAlign: 'center',
      pointerEvents: 'auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }}
  >
    {children}
  </div>
);

const CommunitiesMap = React.memo(({ items = [] }) => {
  const mapRef = useRef(null);

  const [zoom, setZoom] = useState(10);
  const [boundsCluster, setBoundsCluster] = useState(null);

  const [center, setCenter] = useState({ lat: 0, lng: 0 });

  const [infoOpen, setInfoOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const validItems = useMemo(
    () => items.filter((i) => isValidCoordinate(i.latitude, i.longitude)),
    [items],
  );

  const points = useMemo(
    () =>
      validItems.map((item) => ({
        type: 'Feature',
        properties: { ...item, cluster: false },
        geometry: {
          type: 'Point',
          coordinates: [parseFloat(item.longitude), parseFloat(item.latitude)],
        },
      })),
    [validItems],
  );

  const fitToItems = useCallback(() => {
    if (!mapRef.current) return;
    if (!validItems.length) return;

    const latlngbounds = new window.google.maps.LatLngBounds();
    validItems.forEach((i) => {
      latlngbounds.extend(
        new window.google.maps.LatLng(
          parseFloat(i.latitude),
          parseFloat(i.longitude),
        ),
      );
    });

    mapRef.current.fitBounds(latlngbounds);

    const c = latlngbounds.getCenter().toJSON();
    setCenter(c);

    if (validItems.length === 1) {
      mapRef.current.setZoom(11);
      setZoom(11);
    }
  }, [validItems]);

  const handleApiLoaded = useCallback(
    ({ map }) => {
      mapRef.current = map;
      fitToItems();
    },
    [fitToItems],
  );

  useEffect(() => {
    if (!mapRef.current) return;
    fitToItems();
  }, [fitToItems]);

  const handleChange = useCallback(({ zoom, bounds }) => {
    setZoom(zoom);
    if (bounds) {
      setBoundsCluster([
        bounds.nw.lng,
        bounds.se.lat,
        bounds.se.lng,
        bounds.nw.lat,
      ]);
    }
  }, []);

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds: boundsCluster,
    zoom,
    options: { radius: 75, maxZoom: 16 },
  });

  const handleMapClick = useCallback(() => {
    if (infoOpen) setInfoOpen(false);
  }, [infoOpen]);

  const handleMarkerClick = useCallback((item) => {
    setSelectedId(item.id);
    setInfoOpen(true);

    if (mapRef.current) {
      mapRef.current.panTo({
        lat: parseFloat(item.latitude),
        lng: parseFloat(item.longitude),
      });

      setTimeout(() => mapRef.current && mapRef.current.panBy(0, -100), 100);
    }
  }, []);

  useEffect(() => {
    if (selectedId == null) return;
    if (!validItems.some((i) => i.id === selectedId)) {
      setSelectedId(null);
      setInfoOpen(false);
    }
  }, [validItems, selectedId]);

  if (!points.length) {
    return <div>Map cannot be loaded right now, sorry.</div>;
  }

  return (
    <div style={containerStyle}>
      <GoogleMapReact
        options={defaultMapOptions}
        bootstrapURLKeys={{ key: API_KEY }}
        center={center}
        zoom={zoom}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={handleApiLoaded}
        onChange={handleChange}
        onClick={handleMapClick}
      >
        {clusters.map((cluster) => {
          const [lng, lat] = cluster.geometry.coordinates;
          const isCluster = !!cluster.properties.cluster;

          if (isCluster) {
            const count = cluster.properties.point_count;

            return (
              <CustomMarker
                key={`cluster-${cluster.id}`}
                lat={lat}
                lng={lng}
                className='z-10'
              >
                <button
                  type='button'
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!mapRef.current) return;

                    const expansionZoom = Math.min(
                      supercluster.getClusterExpansionZoom(cluster.id),
                      20,
                    );
                    mapRef.current.setZoom(expansionZoom);
                    mapRef.current.panTo({ lat, lng });
                  }}
                  style={{
                    width: 50,
                    height: 50,
                    borderRadius: 9999,
                    border: 0,
                    padding: 0,
                    cursor: 'pointer',
                    backgroundColor: 'transparent',
                    backgroundImage: `url(${clusterMarker})`,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    fontFamily: 'TradeGothic-BoldCondTwenty',
                    color: '#0B2C71',
                    fontSize: 26,
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  aria-label={`Cluster with ${count} items`}
                >
                  {count}
                </button>
              </CustomMarker>
            );
          }

          const item = cluster.properties;
          const isSelected = infoOpen && selectedId === item.id;

          return (
            <CustomMarker
              key={`marker-${item.id}`}
              lat={lat}
              lng={lng}
              className={isSelected ? 'z-50' : 'z-0'}
            >
              {isSelected && (
                <div style={{ marginBottom: 8 }}>
                  <MapCommunityCard {...item} />
                </div>
              )}

              <div
                role='button'
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  handleMarkerClick(item);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    handleMarkerClick(item);
                  }
                }}
                style={{
                  display: 'grid',
                  justifyItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <img
                  src={item.mapIcon?.src}
                  alt={item.title || 'Community'}
                  style={{ width: 45, height: 45 }}
                />
                <div
                  className='gm-style-community-poi-pins'
                  style={{
                    marginTop: 4,
                    fontFamily: 'TradeGothic-BoldCondTwenty',
                    color: '#0B2C71',
                    fontSize: 16,
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.title}
                </div>
              </div>
            </CustomMarker>
          );
        })}
      </GoogleMapReact>
    </div>
  );
});

export default CommunitiesMap;
